import { Fragment } from "react";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { MentionedText } from "@/components/AtMention";
import ListItem from "@/components/ListItem/ListItem";
import ObjectHeading from "@/components/ObjectHeading";
import { CRMNote } from "@/types";
import { NotePriorityValues } from "@/types";
import { MaterialNames } from "@/components/material_symbol_names";
import { toFormat } from "@/lib/dateHelpers";
import Tag from "@/components/Tag/Tag";
import SubSectionHeading from "@/components/SubSectionHeading";
import { capitalize } from "lodash";
import { formatSourceName, formatNotePriority } from "../NotesTable/noteHelpers";
import { getReferenceTypeLabel, getReferenceIcon } from "../ReferencesTable/types";
import { useFlags } from "@/hooks/useFeatureFlags";
import { TASKS_QUERY } from "@/pages/tasks/queries";
import type { TasksQueryResponse, TasksQueryVariables } from "@/pages/tasks/queries";
import { useCanViewTasksBool } from "@/hooks/useCapabilities";

export interface CustomActionButton {
  text: string;
  variant?: "text" | "outlined" | "filled";
  onClick?: () => void;
  testId?: string;
  iconName?: MaterialNames;
  ariaLabel?: string;
  menuOnly?: boolean;
  copyFieldLabel?: string;
  copyFieldId?: string;
  copyButton?: {
    textToCopy: string;
    ariaLabel?: string;
    copyFieldLabel?: string;
    copyFieldId?: string;
    feedback?: "tooltip" | "snackbar" | "none";
    successMessage?: string;
    onCopied?: () => void;
  };
}

interface NoteSideSheetContentProps {
  note: CRMNote;
  customActionButtons?: CustomActionButton[];
  onParentClick?: () => void;
  showSource?: boolean;
}

function getPriorityTag(note: CRMNote) {
  if (!note.priority || note.priority === NotePriorityValues.NONE) {
    return (
      <Tag tagType="surface" tagStyle="strong" size="small">
        {formatNotePriority(NotePriorityValues.NONE)}
      </Tag>
    );
  }

  const priorityType = note.priority === NotePriorityValues.ALERT ? "error" : "information";
  const formattedExpirationDate = note.priorityExpirationDate
    ? toFormat(note.priorityExpirationDate, "MMM dd, yyyy")
    : null;

  return (
    <Tag
      tagType={priorityType}
      tagStyle="strong"
      size="small"
      iconName={formattedExpirationDate ? "schedule" : undefined}
      tooltipText={formattedExpirationDate ? `Expires ${formattedExpirationDate}` : undefined}
    >
      {formatNotePriority(note.priority)}
    </Tag>
  );
}

export default function NoteSideSheetContent({
  note,
  customActionButtons,
  onParentClick,
  showSource = true
}: NoteSideSheetContentProps) {
  const flags = useFlags();
  const navigate = useNavigate();
  const canViewTasks = useCanViewTasksBool();
  const referencesEnabled = !!flags["backoffice-person-note-references"];
  const priorityTag = getPriorityTag(note);
  const formattedUpdatedAt = note.updatedAt ? toFormat(note.updatedAt, "MMM dd, yyyy") : null;

  const { data: tasksData } = useQuery<TasksQueryResponse, TasksQueryVariables>(TASKS_QUERY, {
    variables: {
      first: 25,
      input: {
        referenceableId: note.id,
        referenceableType: ["NOTE"],
        stage: ["OPEN"]
      }
    },
    skip: !canViewTasks || (note.openTasksCount ?? 0) === 0
  });

  const openTasks = (tasksData?.tasks?.nodes ?? []).filter(Boolean);

  return (
    <div>
      <ObjectHeading
        iconName="sticky_note_2"
        title={note.displayTitle || note.title || "Untitled Note"}
        variant="condensed"
        tag={priorityTag || undefined}
        actions={customActionButtons?.map((btn) => ({
          text: btn.text,
          variant: btn.variant,
          onClick: btn.onClick,
          testId: btn.testId,
          iconName: btn.iconName,
          ariaLabel: btn.ariaLabel,
          menuOnly: btn.menuOnly,
          copyFieldLabel: btn.copyFieldLabel,
          copyFieldId: btn.copyFieldId,
          copyButton: btn.copyButton
        }))}
      />

      <div className="flex flex-col gap-2 items-start mt-8">
        <SubSectionHeading>Details</SubSectionHeading>
        <div className="space-y-4 w-full">
          {note.content && (
            <ListItem
              iconName="description"
              iconBackground
              overline="Content"
              headline={note.content}
              longContent={true}
            />
          )}
          <ListItem
            iconName="person"
            iconBackground
            overline="Author"
            headline={note.authorName || "--"}
            supportingText={note.author?.email || null}
            copyable={!!note.author?.email}
            copyText={note.author?.email || undefined}
          />
          {formattedUpdatedAt && (
            <ListItem iconName="calendar_today" iconBackground overline="Modified" headline={formattedUpdatedAt} />
          )}
          {note.visibility && (
            <ListItem
              iconName="visibility"
              iconBackground
              overline="Visibility"
              headline={capitalize(note.visibility)}
            />
          )}
        </div>
      </div>

      {showSource && note.notableType && (
        <div className="flex flex-col gap-2 items-start mt-8">
          <SubSectionHeading>Source</SubSectionHeading>
          <div className="space-y-4 w-full">
            <ListItem
              iconName="folder"
              iconBackground
              headline={formatSourceName(note.notableType)}
              {...(onParentClick ? { action: onParentClick } : {})}
            />
          </div>
        </div>
      )}

      {canViewTasks && openTasks.length > 0 && (
        <div className="flex flex-col gap-2 items-start mt-8" data-testid="note-open-tasks-section">
          <SubSectionHeading badgeCount={openTasks.length}>Open Tasks</SubSectionHeading>
          <div className="space-y-4 w-full">
            {openTasks.map((task) => (
              <Fragment key={task.id}>
                <ListItem
                  iconName="inventory"
                  iconBackground
                  headline={<MentionedText content={task.name || "--"} />}
                  supportingText={
                    [
                      task.dueDate ? `Due: ${toFormat(task.dueDate, "MMM dd, yyyy")}` : null,
                      task.assignedTo?.name ? `Assignee: ${task.assignedTo.name}` : null
                    ]
                      .filter(Boolean)
                      .join("\n") || null
                  }
                  action={() => navigate(`/tasks/${task.id}?highlightReference=${note.id}`)}
                />
              </Fragment>
            ))}
          </div>
        </div>
      )}

      {referencesEnabled && note.references && note.references.length > 0 && (
        <div className="flex flex-col gap-2 items-start mt-8" data-testid="note-references-section">
          <SubSectionHeading>References</SubSectionHeading>
          <div className="space-y-4 w-full">
            {note.references.map((ref) => {
              const rawUrl = ref.__typename === "BackofficePersonProfileDocument" ? ref.url : undefined;
              const safeUrl = rawUrl && /^https?:\/\//i.test(rawUrl) ? rawUrl : undefined;
              const dateStr = ref.updatedAt || ref.createdAt;
              const formattedDate = dateStr ? toFormat(dateStr, "MMM dd, yyyy") : "";

              return (
                <ListItem
                  key={ref.id}
                  useAvatar
                  iconName={getReferenceIcon(ref.__typename)}
                  overline={getReferenceTypeLabel(ref.__typename)}
                  headline={ref.filename || ref.name || "--"}
                  supportingText={formattedDate}
                  {...(safeUrl
                    ? {
                        action: () => window.open(safeUrl, "_blank", "noopener,noreferrer"),
                        actionIcon: "open_in_new" as const
                      }
                    : {})}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
